/**
 * URLs legibles con idioma en la ruta (plan 12b, S1/S2): `/hu/p/levis-501-farmer-32-{id}`.
 * El id va al final y es lo único que cuenta: si el título cambia, la URL vieja sigue
 * resolviendo (y el cliente canoniza al slug nuevo). El slug es ASCII para que todas las
 * variantes de un anuncio compartan una misma forma en cualquier idioma.
 */
import type { LanguageCode } from '../firestore/primitives.js';

const MAX_SLUG = 60;

/** "Levi's 501 Farmer 32" → "levis-501-farmer-32". Sin acentos, solo [a-z0-9-]. */
export function slugify(text: string): string {
  const ascii = text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[łŁ]/g, 'l')
    .replace(/[đĐ]/g, 'd')
    .replace(/['’`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (ascii.length <= MAX_SLUG) return ascii;
  const cut = ascii.slice(0, MAX_SLUG);
  const lastDash = cut.lastIndexOf('-');
  return lastDash > 20 ? cut.slice(0, lastDash) : cut;
}

/** `/{lang}/p/{slug}-{id}` (sin slug si el título queda vacío: `/{lang}/p/{id}`). */
export function productUrl(lang: LanguageCode, product: { id: string; title: string }): string {
  const slug = slugify(product.title);
  return `/${lang}/p/${slug ? `${slug}-${product.id}` : product.id}`;
}

/**
 * Extrae el id del segmento `{slug}-{id}`. Los ids de Firestore de la 1.0 son alfanuméricos
 * (sin guiones), así que el id es lo que hay tras el último guion.
 */
export function productIdFromSlug(slugId: string): string {
  const i = slugId.lastIndexOf('-');
  return i === -1 ? slugId : slugId.slice(i + 1);
}

/** Ruta de la 1.0 (`/product/{id}`) → ruta 2.0 en un idioma; la tabla de 301 del servidor usa esto. */
export function legacyProductUrl(
  lang: LanguageCode,
  product: { id: string; title: string },
): { from: string; to: string } {
  return { from: `/product/${product.id}`, to: productUrl(lang, product) };
}
